import type { FC, ReactNode } from 'react'
import type { SimFactory } from '@/types'
import type { SimulationKey } from '@/constants/landing'

import { cn } from '@/lib/utils'
import { createDoublePendulum } from '@/lib/sims/double-pendulum'
import { createOrbit } from '@/lib/sims/orbit'
import { createInterference } from '@/lib/sims/interference'
import { createDipole } from '@/lib/sims/dipole'
import { createGas } from '@/lib/sims/gas'
import { createOrbital } from '@/lib/sims/orbital'
import { Reveal } from '@/components/generals/reveal'
import { SimCanvas } from '@/components/landing/sim-canvas'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

import { simulationCards } from '@/constants/landing'

const factories: Record<SimulationKey, SimFactory> = {
    pendulum: createDoublePendulum,
    orbit: createOrbit,
    interference: createInterference,
    dipole: createDipole,
    gas: createGas,
    orbital: createOrbital
}

const legends: Partial<Record<SimulationKey, { swatch: string, label: string }[]>> = {
    orbit: [
        { swatch: 'bg-signal', label: "Star" },
        { swatch: 'bg-brand', label: "Planet" }
    ],
    dipole: [
        { swatch: 'bg-signal', label: "+q" },
        { swatch: 'bg-brand', label: "−q" }
    ],
    gas: [
        { swatch: 'bg-[#9aa0ae]', label: "Slow" },
        { swatch: 'bg-brand', label: "Typical" },
        { swatch: 'bg-signal', label: "Fast" }
    ],
    orbital: [
        { swatch: 'bg-brand', label: "+ phase" },
        { swatch: 'bg-signal', label: "− phase" }
    ]
}

export const SimulationsSection: FC = (): ReactNode => {
    return (
        <section id='simulations' className='scroll-mt-20 py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        index="01"
                        label="Simulations"
                        title={<>Every law of nature, <Accent>running live.</Accent></>}
                        description="Not videos. Not diagrams. Each simulation is solved frame by frame as you watch, so what you see is exactly what the equations predict."
                    />

                    <p className='max-w-xs border-l border-border pl-5 text-sm leading-relaxed text-muted-foreground'>
                        Every figure in this section is computing physics right now, in your browser. Nothing here is pre-rendered.
                    </p>
                </Reveal>

                <div className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5'>
                    {simulationCards.map((card, index) => (
                        <Reveal key={card.key} delay={(index % 3) * 90} className='h-full'>
                            <article className='group flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-2 elevate transition-shadow duration-500 hover:elevate-lg'>
                                <div className='relative aspect-[16/11] overflow-hidden rounded-[22px] border border-hairline bg-white'>
                                    <div
                                        aria-hidden='true'
                                        className='absolute inset-0 bg-grid opacity-70 [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_85%)]'
                                    />

                                    <SimCanvas create={factories[card.key]} label={card.caption} className='absolute inset-0' />

                                    <span className='absolute left-3.5 top-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
                                        {card.figure}
                                    </span>

                                    <span className='absolute right-3 top-2.5 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-white/85 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink backdrop-blur'>
                                        <span className='size-1.5 animate-pulse rounded-full bg-brand' />
                                        Live
                                    </span>

                                    {legends[card.key] && (
                                        <ul className='absolute bottom-2.5 left-3 flex flex-wrap items-center gap-x-3 gap-y-1'>
                                            {legends[card.key]?.map(item => (
                                                <li key={item.label} className='flex items-center gap-1.5 rounded-full bg-white/85 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground backdrop-blur'>
                                                    <span className={cn('size-1.5 rounded-full', item.swatch)} />
                                                    {item.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className='flex flex-1 flex-col px-4 pb-4 pt-5'>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'>
                                        {card.field}
                                        <span className='mx-2 text-border'>/</span>
                                        {card.count} simulations
                                    </p>

                                    <h3 className='mt-2.5 text-xl font-semibold tracking-[-0.02em] text-ink'>
                                        {card.title}
                                    </h3>

                                    <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                        {card.description}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    )
}
